// 버핏스코어 · 유사기업 조회 페이지 API — officejs(LabPage/ValuationPage) 흐름의
// 웹 이식. 모든 호출은 에이전트 프록시(/agent/*)를 거친다 (로그인 게이트 뒤).
import { api } from "./api";

interface Proxied<T = unknown> {
  ok: boolean;
  data?: T;
  message?: string;
}

async function post<T = unknown>(path: string, payload: object): Promise<T> {
  const d = await api<Proxied<T>>(path, { method: "POST", body: JSON.stringify({ payload }) });
  if (!d.ok) throw new Error(d.message || "요청에 실패했습니다.");
  return d.data as T;
}

async function get<T = unknown>(path: string): Promise<T> {
  const d = await api<Proxied<T>>(path);
  if (!d.ok) throw new Error(d.message || "요청에 실패했습니다.");
  return d.data as T;
}

// ── 공통 폴링 — officejs 와 같은 규약: processing 동안 {status, step[, partial]},
//    완료는 done, task not found 는 폴링 경합이므로 초기 몇 회 무시 ──────────
export interface PollHandle {
  cancel: () => void;
}

export function pollTask<T>(opts: {
  statusPath: (taskId: string) => string;   // /agent/... 상태 경로
  taskId: string;
  onStep?: (step: string, partial?: unknown) => void;
  intervalMs?: number;
  timeoutMs?: number;
  resolve: (result: T) => void;
  reject: (err: Error) => void;
}): PollHandle {
  const interval = opts.intervalMs ?? 2500;
  const deadline = Date.now() + (opts.timeoutMs ?? 10 * 60_000);
  let cancelled = false;
  let notFound = 0;

  const tick = async () => {
    if (cancelled) return;
    if (Date.now() > deadline) {
      opts.reject(new Error("시간이 초과되었습니다. 잠시 후 다시 시도해 주세요."));
      return;
    }
    try {
      const d = await api<Proxied<Record<string, unknown>> & { done?: boolean; result?: T; step?: string; not_found?: boolean }>(
        opts.statusPath(opts.taskId),
      );
      if (cancelled) return;
      // 에이전트의 /agent/peer/status 는 {done, result|step} 로 이미 정규화돼 있고,
      // /agent/buffett/status 는 valuation-2 응답을 그대로 싣는다 — 둘 다 처리.
      if (d.done && d.result !== undefined) {
        opts.resolve(d.result as T);
        return;
      }
      if (d.ok === false) {
        if (d.not_found || /task not found/.test(d.message || "")) {
          // 방금 시작한 태스크가 아직 스토어에 없을 수 있다 — 몇 회는 참는다
          if (++notFound <= 5) {
            setTimeout(tick, interval);
            return;
          }
        }
        opts.reject(new Error(d.message || "작업이 실패했습니다."));
        return;
      }
      const data = (d.data || d) as Record<string, unknown>;
      const step = String(d.step ?? data.step ?? "");
      if (step && opts.onStep) opts.onStep(step, data.partial);
      // 버핏 프록시: 완료 시 data 가 결과 본문(step 없음)
      if (data && !("status" in data) && !("step" in data) && Object.keys(data).length > 2) {
        opts.resolve(data as T);
        return;
      }
      setTimeout(tick, interval);
    } catch (e) {
      if (cancelled) return;
      const msg = e instanceof Error ? e.message : String(e);
      if (/task not found/.test(msg) && ++notFound <= 5) {
        setTimeout(tick, interval);
        return;
      }
      opts.reject(e instanceof Error ? e : new Error(msg));
    }
  };
  setTimeout(tick, interval);
  return { cancel: () => { cancelled = true; } };
}

// ── 종목 검색 (KR 상장) ────────────────────────────────────────────
export interface CompanyHit {
  symbol: string;   // KRX:005930
  name: string;
}

export async function searchCompany(q: string): Promise<CompanyHit[]> {
  const d = await api<{ ok: boolean; items?: CompanyHit[] }>(
    "/agent/company-search?q=" + encodeURIComponent(q));
  return d.ok ? d.items || [] : [];
}

/** 리포트 메뉴의 기업 검색 — 상장·비상장(외감)을 함께 매칭한다 */
export interface ReportCompanyHit {
  symbol: string;
  name: string;
  listed: boolean;
  industry?: string;
  /** 비상장 동명 법인 구분용 — 대표자·도로명주소·외감 여부(서버 GetEntitySummary) */
  ceo?: string;
  address?: string;
  audit?: string;
}

export async function searchReportCompany(q: string): Promise<ReportCompanyHit[]> {
  const d = await api<{ ok: boolean; items?: ReportCompanyHit[] }>(
    "/agent/report-company-search?q=" + encodeURIComponent(q));
  return d.ok ? d.items || [] : [];
}

/** 리포트 생성 — 서버 태스크 시작 → task_id. 진행은 reportStatus 폴링 */
export interface ReportResult {
  subject: string; symbol: string; kind: string; pages: number; asof: string;
  listed: boolean; toc: [string, string][]; token: string;
  /** S3 영구 URL — '내 리포트'의 이전 보고서 보기용 (업로드 실패 시 빈 값) */
  url?: string;
}
export async function reportGenerate(symbol: string, name: string, listed: boolean): Promise<string> {
  const d = await api<{ ok: boolean; task_id?: string; message?: string }>(
    "/agent/report2/generate",
    { method: "POST", body: JSON.stringify({ payload: { symbol, name, listed } }) });
  if (!d.ok || !d.task_id) throw new Error(d.message || "생성 시작에 실패했어요");
  return d.task_id;
}
export async function reportStatus(taskId: string): Promise<{
  step: string; done: boolean; error: string; result: ReportResult | null;
}> {
  const d = await api<{ ok: boolean; data?: { step: string; done: boolean; error: string; result: ReportResult | null }; message?: string }>(
    "/agent/report2/status/" + encodeURIComponent(taskId));
  if (!d.ok || !d.data) throw new Error(d.message || "상태 조회 실패");
  return d.data;
}

// ── 버핏스코어 ─────────────────────────────────────────────────────
export interface BuffettMoat {
  score?: number;
  evidence?: string;
}

export interface BuffettAnalysis {
  moatAssessment?: Record<string, BuffettMoat>;
  investmentSummary?: string | null;
  [k: string]: unknown;
}

export async function buffettStart(payload: {
  company_name: string;
  symbol?: string;
  market?: "kr" | "us";
  us_10k_context?: string;
  /** true 면 7일 캐시를 무시하고 새로 분석 ('다시 분석' 버튼) */
  force?: boolean;
}): Promise<{ task_id?: string; result?: BuffettAnalysis }> {
  const data = await post<Record<string, unknown>>("/agent/buffett/analyze", payload);
  if (data && (data as { task_id?: string }).task_id) {
    return { task_id: (data as { task_id: string }).task_id };
  }
  return { result: data as BuffettAnalysis };   // cache hit — 즉시 결과
}

/** 생성일 스냅샷 — 딥링크(?asof=) 영구 복원. 7일 캐시 만료와 무관하다. */
export async function buffettSnapshot(companyName: string, date: string): Promise<BuffettAnalysis | null> {
  try {
    return await get<BuffettAnalysis>(
      `/agent/buffett/snapshot?company_name=${encodeURIComponent(companyName)}&date=${encodeURIComponent(date)}`);
  } catch {
    return null;   // 스냅샷이 없으면 호출부가 라이브 조회로 폴백
  }
}

export function buffettStatusPath(taskId: string): string {
  return "/agent/buffett/status/" + encodeURIComponent(taskId);
}

/** 실행 중단 — 폴링만 끊으면 서버 작업은 계속 돌아 캐시에 저장된다. */
export async function buffettCancel(taskId: string): Promise<void> {
  await api("/agent/buffett/cancel/" + encodeURIComponent(taskId), { method: "POST" });
}

export async function peerCancel(taskId: string): Promise<void> {
  await api("/agent/peer/cancel/" + encodeURIComponent(taskId), { method: "POST" });
}

export async function buffettUs10k(ticker: string): Promise<{
  context_text?: string;
  business_description?: string;
  revenue_structure?: string;
} | null> {
  try {
    return await get("/agent/buffett/us-10k/" + encodeURIComponent(ticker));
  } catch {
    return null;   // enrichment 는 부가 정보 — 실패해도 분석은 진행
  }
}

export async function buffettPdf(payload: object): Promise<string> {
  const data = await post<{ url?: string }>("/agent/buffett/pdf", payload);
  if (!data?.url) throw new Error("PDF URL 을 받지 못했습니다.");
  return data.url;
}

// ── 버핏 채점 ──────────────────────────────────────────────────────
// 채점의 단일 소스는 백엔드 valuation_agent/buffett_score.py — 서버 프록시가
// 모든 응답에 scoreSummary 를 주입한다. 아래 로컬 계산은 scoreSummary 가 없는
// 구(舊) 캐시 응답용 폴백이며, 백엔드 표(RATING_TABLE)와 항상 같아야 한다.
export interface BuffettGate { key: string; label: string; passed: boolean | null; basis: string }
export interface BuffettScoreSummary {
  score100: number; rating: string; action: string;
  moatCount: number; missingMoats: string[];
  gates: BuffettGate[]; gatesPassed: number;
}

export function buffettSummary(a: BuffettAnalysis | null): BuffettScoreSummary | null {
  const s = (a as { scoreSummary?: BuffettScoreSummary } | null)?.scoreSummary;
  return s && typeof s.score100 === "number" ? s : null;
}

// 폴백 — 평가된 축의 평균 × 10 (6축 모두 있으면 기존 Σ×10/6 과 동일)
export function buffettScore100(a: BuffettAnalysis | null): number | null {
  const fromServer = buffettSummary(a);
  if (fromServer) return fromServer.score100;
  const moat = a?.moatAssessment;
  if (!moat) return null;
  const scores = Object.values(moat)
    .map((m) => (typeof m?.score === "number" ? m.score : null))
    .filter((v): v is number => v != null);
  if (!scores.length) return null;
  return Math.min(100, Math.round((scores.reduce((x, y) => x + y, 0) / scores.length) * 10));
}

// 백엔드 RATING_TABLE 과 동일한 5구간 — 임계값 80/60/40/20/0
export function buffettRating(score: number): { rating: string; action: string } {
  if (score >= 80) return { rating: "위대한 기업", action: "사서 절대 팔지 마라. 복리 머신." };
  if (score >= 60) return { rating: "훌륭한 기업", action: "적정 가격의 훌륭한 기업. 안전마진 확보 후 매수." };
  if (score >= 40) return { rating: "좋은 기업", action: "좋지만 위대하지는 않다. 확실한 할인 시에만 매수." };
  if (score >= 20) return { rating: "평범한 기업", action: "구조적 역풍. 회피 권장." };
  return { rating: "능력범위 밖", action: "지속 가능한 우위 없음. 어떤 가격도 충분히 낮지 않다." };
}

/** 점수 색상 단계 — 등급표와 같은 임계값에서 파생 (별도 상수 금지). */
export function buffettTier(score: number): "good" | "mid" | "bad" {
  return score >= 60 ? "good" : score >= 40 ? "mid" : "bad";
}

/** 완료된 유사기업/산업분류 조회의 영구 스냅샷 — 딥링크(?rid=) 복원. */
export async function peerResult<T = Record<string, unknown>>(rid: string): Promise<T | null> {
  try {
    return await get<T>(`/agent/peer/result/${encodeURIComponent(rid)}`);
  } catch {
    return null;
  }
}

/** 공시 선례 검색 결과 → 엑셀 조서 (S3 URL). */
export async function precedentExcel(payload: { business_text: string; results: unknown[] }): Promise<string> {
  const data = await post<{ url?: string }>("/agent/peer/major-report/excel", payload);
  if (!data?.url) throw new Error("엑셀 URL 을 받지 못했습니다.");
  return data.url;
}

// ── 유사기업 (사업내용 AI) ─────────────────────────────────────────
export async function peerStart(payload: {
  business_text: string;
  markets: string[];
  top_k: number;
  company_name?: string;
  symbol?: string;
  valuation_date?: string;
}): Promise<string> {
  const d = await api<{ ok: boolean; task_id?: string; message?: string }>(
    "/agent/peer/select/async", { method: "POST", body: JSON.stringify({ payload }) });
  if (!d.ok || !d.task_id) throw new Error(d.message || "선정을 시작하지 못했습니다.");
  return d.task_id;
}

export function peerStatusPath(taskId: string): string {
  return "/agent/peer/status/" + encodeURIComponent(taskId);
}

export async function peerExcel(payload: object): Promise<string> {
  const data = await post<{ url?: string }>("/agent/peer/select/excel", payload);
  if (!data?.url) throw new Error("엑셀 URL 을 받지 못했습니다.");
  return data.url;
}

export async function peerPdf(payload: object): Promise<string> {
  const data = await post<{ url?: string }>("/agent/peer/select/pdf", payload);
  if (!data?.url) throw new Error("PDF URL 을 받지 못했습니다.");
  return data.url;
}

// ── 산업분류(KSIC) ─────────────────────────────────────────────────
export interface IndustrySuggestion {
  industry_name: string;
  ksic_code?: string;
  listed_count?: number;
}

export async function industrySuggest(payload: {
  business_text: string;
  target_symbol?: string;
  max_suggestions?: number;
}): Promise<IndustrySuggestion[]> {
  const data = await post<{ suggestions?: IndustrySuggestion[] }>(
    "/agent/peer/industry/suggest", { max_suggestions: 24, ...payload });
  return data?.suggestions || [];
}

export async function industryCatalog(): Promise<string[]> {
  const data = await get<{ industries?: unknown[] }>("/agent/peer/industry/catalog?limit=0");
  return (data?.industries || [])
    .map((x) => typeof x === "string" ? x
      : String((x as { industry_name?: string; name?: string })?.industry_name
          || (x as { name?: string })?.name || ""))
    .filter(Boolean);
}

export async function industryScreenStart(payload: object): Promise<string> {
  const data = await post<{ task_id?: string }>("/agent/peer/industry-screen/async", payload);
  if (!data?.task_id) throw new Error("스크리닝을 시작하지 못했습니다.");
  return data.task_id;
}

/** 산업분류 스크리닝 엑셀 — 밸류에이전트 서버가 만들어 S3 에 두고, 파일 내용(base64)도 함께 준다 */
export async function industryExcel(payload: object): Promise<{ url: string; filename: string; contentBase64?: string }> {
  const data = await post<{ url?: string; filename?: string; contentBase64?: string }>("/agent/peer/industry-screen/excel", payload);
  if (!data?.url) throw new Error("엑셀 URL 을 받지 못했습니다.");
  return { url: data.url, filename: data.filename || "산업분류_비교회사선정.xlsx", contentBase64: data.contentBase64 };
}

export async function industryPdf(payload: object): Promise<string> {
  const data = await post<{ url?: string }>("/agent/peer/industry-screen/pdf", payload);
  if (!data?.url) throw new Error("PDF URL 을 받지 못했습니다.");
  return data.url;
}

// ── 주요사항보고서 선례 ────────────────────────────────────────────
export interface PrecedentResult {
  total_records?: number;
  result_count?: number;
  results?: Record<string, unknown>[];
}

export async function precedentSearch(payload: {
  business_text?: string;
  year_from?: number;
  year_to?: number;
  evaluator?: string;
  top_k?: number;
}): Promise<PrecedentResult> {
  return await post<PrecedentResult>("/agent/peer/major-report/search", payload);
}

// ── 표시 유틸 ──────────────────────────────────────────────────────
export function fmtNum(n: unknown): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return "–";
  return n.toLocaleString("ko-KR");
}

export function elapsedLabel(startedAt: number): string {
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
